import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {IconButton, Tooltip} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import {RootState} from "../../../store";
import {makeLink} from "../../common/languages-helper";
import api from "../../api/api";
import {toast} from "react-toastify";

import InvitesTable from "./InvitesTable";
import CreateInvite from "./popups/CreateInvite";
import InviteDetails from "./popups/InviteDetails";
import EditInvite from "./popups/EditInvite";
import {copyInviteLink} from "./invite-copy-functions";
import {TherapyInvite} from "./therapy-invite";

import YellowButton from "../../common/buttons/YellowButton";
import Footer from "../../navigation/Footer";
import {
    ProfileSearcherCard,
    StatsColumn,
    PromoBanner,
} from "./DashboardCards";

import "./TherapistDashboard.css";

type SessionTab = "individual" | "group";

const TherapistDashboard: React.FC = () => {
    const {t} = useTranslation();
    const profileData = useSelector((state: RootState) => state.profile);

    const [rows, setRows] = useState<TherapyInvite[]>([]);
    const [detailsPopupOpen, setDetailsPopupOpen] = useState(false);
    const [createPopupOpen, setCreatePopupOpen] = useState(false);
    const [editPopupOpen, setEditPopupOpen] = useState(false);
    const [currentInviteDetails, setCurrentInviteDetails] = useState<TherapyInvite | null>(null);
    const [inviteToEdit, setInviteToEdit] = useState<TherapyInvite | null>(null);
    const [activeTab, setActiveTab] = useState<SessionTab>("individual");

    const BANNER_LS_KEY = "emdrlink.promoBannerDismissed";
    const [bannerOpen, setBannerOpen] = useState(() => {
        try {
            return localStorage.getItem(BANNER_LS_KEY) !== "1";
        } catch {
            return true;
        }
    });

    const dismissBanner = () => {
        setBannerOpen(false);
        try {
            localStorage.setItem(BANNER_LS_KEY, "1");
        } catch {
            /* ignore */
        }
    };

    // Group sessions are a paid feature — locked on the free tier.
    const groupLocked = profileData.plan === "free";

    const refreshInvites = async () => {
        const invitesData = await api().get("invites");
        setRows(invitesData.data);
    };

    useEffect(() => {
        if (!createPopupOpen) {
            refreshInvites();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createPopupOpen]);

    const handleStartSession = (invite: TherapyInvite) => {
        /* eslint-disable no-restricted-globals */
        window.open(makeLink("/session-") + invite._id, "therapist-window", `
            directories=0,titlebar=0,toolbar=0,location=0,status=0,
            menubar=0,scrollbars=no,resizable=no,
            width=400,height=` + screen.height);
    };

    const handleCopyLink = (invite: TherapyInvite) => {
        copyInviteLink(invite);
    };

    const handleShowDetails = (invite: TherapyInvite) => {
        setCurrentInviteDetails(invite);
        setDetailsPopupOpen(true);
    };

    const handleEdit = (invite: TherapyInvite) => {
        setInviteToEdit(invite);
        setEditPopupOpen(true);
    };

    const handleDelete = async (invite: TherapyInvite) => {
        /* eslint-disable no-restricted-globals */
        if (confirm(t("Are you sure you want to delete invite?"))) {
            await api().delete("invites/" + invite._id);
            toast.success(t("Invite has been deleted successfully"));
            refreshInvites();
        }
    };

    const tabButtonClass = (tab: SessionTab) =>
        "session-tab" + (activeTab === tab ? " active" : "");

    return (
        <>
        <div className="dashboard">
            {bannerOpen && (
                <div className="dashboard-banner">
                    <PromoBanner />
                    <IconButton
                        onClick={dismissBanner}
                        aria-label="close"
                        className="banner-close"
                        sx={{color: "#F8FBFC"}}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>
            )}

            <div className="dashboard-cards">
                <ProfileSearcherCard />
                <StatsColumn activeRooms={rows.length} />
            </div>

            <div className="sessions-section">
                <div className="sessions-header">
                    <span className="sessions-title">{t("Your sessions")}</span>
                    <button
                        className={tabButtonClass("individual")}
                        onClick={() => setActiveTab("individual")}
                        role="tab"
                        aria-selected={activeTab === "individual"}
                    >
                        {t("Individual")}
                    </button>
                    {groupLocked ? (
                        <Tooltip title={t("Group sessions will be available soon.")}>
                            <button
                                className="session-tab locked"
                                aria-disabled="true"
                                onClick={(e) => e.preventDefault()}
                            >
                                {t("Group")}
                                <LockOutlinedIcon sx={{fontSize: 15, ml: 0.5, verticalAlign: "-2px"}} />
                            </button>
                        </Tooltip>
                    ) : (
                        <button
                            className={tabButtonClass("group")}
                            onClick={() => setActiveTab("group")}
                            role="tab"
                            aria-selected={activeTab === "group"}
                        >
                            {t("Group")}
                        </button>
                    )}
                    <span className="sessions-spacer" />
                    <YellowButton onClick={() => setCreatePopupOpen(true)}>{t("New session")}</YellowButton>
                </div>

                {activeTab === "individual" ? (
                    rows.length > 0 ? (
                        <InvitesTable
                            rows={rows}
                            onStart={handleStartSession}
                            onCopyLink={handleCopyLink}
                            onShowDetails={handleShowDetails}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    ) : (
                        <div className="sessions-empty">
                            <p style={{marginTop: 0}}>{t("You don't have any sessions yet.")}</p>
                            <YellowButton onClick={() => setCreatePopupOpen(true)}>{t("New session")}</YellowButton>
                        </div>
                    )
                ) : (
                    <div className="sessions-empty">{t("Group sessions are not available yet.")}</div>
                )}
            </div>

            <CreateInvite createPopupOpen={createPopupOpen} setCreatePopupOpen={setCreatePopupOpen} />
            <EditInvite
                invite={inviteToEdit}
                open={editPopupOpen}
                setOpen={setEditPopupOpen}
                onSaved={refreshInvites}
            />
            <InviteDetails
                invite={currentInviteDetails}
                detailsPopupOpen={detailsPopupOpen}
                setDetailsPopupOpen={setDetailsPopupOpen}
            />

        </div>
        <Footer />
        </>
    );
};

export default TherapistDashboard;
